
import { useState, useRef } from "react";
import { UploadCloud, AlertCircle, FileText, Check, X, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { CSVImportDropzone } from "@/components/admin/imports/CSVImportDropzone";
import { CSVPreviewTable } from "@/components/admin/imports/CSVPreviewTable";
import { ColumnMappingForm } from "@/components/admin/imports/ColumnMappingForm";
import { ImportSummary } from "@/components/admin/imports/ImportSummary";
import { useCSVImport } from "@/hooks/useCSVImport";
import { ImportSettings } from "@/components/admin/imports/ImportSettings";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export const ImportQuotesTab = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [enableDeletion, setEnableDeletion] = useState(false);
  
  const {
    file,
    parsedData,
    columnMapping,
    importSettings,
    previewData,
    importProgress,
    importResults,
    isImporting,
    isLoading,
    error,
    setFile,
    updateColumnMapping,
    updateImportSettings,
    startImport,
    cancelImport,
    validateFile,
    downloadTemplate
  } = useCSVImport();

  const handleFileChange = async (selectedFile: File | null) => {
    if (!selectedFile) return;
    
    try {
      setFile(selectedFile);
      // Validate and parse will be handled within the hook
      setActiveTab("preview");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error parsing CSV",
        description: err instanceof Error ? err.message : "Unknown error occurred."
      });
    }
  };

  const handleImport = async () => {
    try {
      // Update import settings with deletion flag
      const updatedSettings = {
        ...importSettings,
        enableDeletion
      };
      
      updateImportSettings(updatedSettings);
      
      await startImport();
      setActiveTab("results");
      toast({
        title: "Import completed",
        description: `Successfully processed ${importResults?.successful || 0} quotes.`
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Import failed",
        description: err instanceof Error ? err.message : "Unknown error occurred."
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Import Quotes</h2>
        <Button onClick={downloadTemplate} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Download Template
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 mb-6">
          <TabsTrigger value="upload">1. Upload File</TabsTrigger>
          <TabsTrigger value="preview" disabled={!file}>2. Preview & Map</TabsTrigger>
          <TabsTrigger value="results" disabled={!importResults}>3. Results</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <Card>
            <CardHeader>
              <CardTitle>Upload CSV File</CardTitle>
              <CardDescription>
                Upload a CSV file containing quotes to import. The file should have columns for quote content, author, and categories.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CSVImportDropzone onFileSelect={handleFileChange} />
              
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Import Settings</CardTitle>
              <CardDescription>Configure how your CSV file should be processed</CardDescription>
            </CardHeader>
            <CardContent>
              <ImportSettings 
                settings={importSettings}
                onChange={updateImportSettings}
              />
              
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="enable-deletion" 
                    checked={enableDeletion}
                    onCheckedChange={setEnableDeletion}
                  />
                  <Label htmlFor="enable-deletion" className="font-medium">Enable Quote Deletion</Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  When enabled, any quote with "delete" in the categories column will be deleted from the database.
                </p>
                {enableDeletion && (
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Caution</AlertTitle>
                    <AlertDescription>
                      This will permanently delete quotes from the database. Please review your CSV file carefully before importing.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Column Mapping</CardTitle>
              <CardDescription>
                Map the columns in your CSV file to the appropriate quote fields
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ColumnMappingForm 
                columns={parsedData?.meta?.fields || []}
                mapping={columnMapping}
                onChange={updateColumnMapping}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview Data</CardTitle>
              <CardDescription>
                Review how your data will be imported
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-8 text-center">Loading preview...</div>
              ) : (
                <>
                  <CSVPreviewTable 
                    data={previewData}
                    mapping={columnMapping}
                    showDeletionWarning={enableDeletion}
                  />
                  
                  <div className="flex justify-end mt-6 space-x-4">
                    <Button variant="outline" onClick={() => setActiveTab("upload")}>
                      Back
                    </Button>
                    <Button 
                      onClick={handleImport} 
                      disabled={isImporting || !parsedData}
                    >
                      {isImporting ? "Importing..." : "Start Import"}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results">
          <Card>
            <CardHeader>
              <CardTitle>Import Results</CardTitle>
              <CardDescription>
                Summary of the import operation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isImporting ? (
                <div className="space-y-4">
                  <p>Importing quotes... Please do not close this page.</p>
                  <Progress value={importProgress} className="w-full" />
                  <p className="text-sm text-muted-foreground">
                    {Math.round(importProgress)}% complete
                  </p>
                  <Button variant="outline" onClick={cancelImport}>
                    Cancel Import
                  </Button>
                </div>
              ) : (
                <ImportSummary results={importResults} showDeletions={enableDeletion} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
